import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Check, Copy, FileJson, X } from "lucide-react";

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/misc/Dialog";
import { Button } from "../../components/misc/Button";
import Spinner from "../../components/misc/Spinner";
import CustomToast from "../../components/misc/CustomToast";
import { getCaptionRenderAudit } from "./actions";
import { CaptionRenderAudit } from "./types";

interface CaptionAuditDialogProps {
    // Null when closed. Fetching is keyed on this, so the artifact is pulled ONLY when a
    // reviewer actually opens a render — it carries the listing text and every raw model
    // response, which is not something to load for rows nobody has looked at.
    captionRenderId: string | null;
    onClose: () => void;
}

/**
 * The full debug record for one caption run.
 *
 * Shown as raw JSON on purpose. Every caption investigation so far has needed a different
 * field, so a tidy summary would just be a fresh guess about which one matters next — and
 * the whole point of keeping this data was to stop guessing. It is also meant to be
 * copied out whole and handed to an LLM, which is what was asked for.
 */
const CaptionAuditDialog: React.FC<CaptionAuditDialogProps> = ({
    captionRenderId,
    onClose,
}) => {
    const [audit, setAudit] = useState<CaptionRenderAudit | null>(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!captionRenderId) {
            setAudit(null);
            setCopied(false);
            // Reset loading too: without it, a dialog closed mid-request reopens showing
            // a spinner that nothing will ever clear.
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getCaptionRenderAudit(captionRenderId)
            .then((data) => {
                if (!cancelled) setAudit(data);
            })
            .catch(() => {
                // Guarded, like the success path. A slow request for a render the admin
                // has already closed — or moved on from — must not throw an error toast
                // over whatever they are looking at now.
                if (!cancelled) {
                    toast.error(
                        <CustomToast
                            message="Couldn't load the caption debug record"
                            type="error"
                        />,
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [captionRenderId]);

    const json = audit ? JSON.stringify(audit, null, 2) : "";

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(json);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(<CustomToast message="Couldn't copy" type="error" />);
        }
    }, [json]);

    return (
        <Dialog
            open={captionRenderId !== null}
            onOpenChange={(open) => !open && onClose()}
        >
            <DialogContent className="relative max-w-4xl bg-white">
                {/* The shared DialogContent ships no close affordance, so add one. */}
                <DialogClose className="absolute right-3 top-3 z-10 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-primary focus:outline-none">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </DialogClose>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 pr-8 text-left text-sm font-semibold">
                        <FileJson className="h-4 w-4" />
                        Caption debug record
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <Spinner />
                    </div>
                ) : audit ? (
                    <>
                        {/* The few facts worth reading before the JSON — mainly WHICH
                            prompt wrote this — with Copy on the same line so it never
                            collides with the title or the close button. */}
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                                <span>
                                    Prompt{" "}
                                    <span className="font-semibold text-primary">
                                        {audit.promptVersion}
                                    </span>
                                </span>
                                <span>
                                    Model{" "}
                                    <span className="font-semibold text-primary">
                                        {audit.captionModel ?? "—"}
                                    </span>
                                </span>
                                <span>
                                    Tier{" "}
                                    <span className="font-semibold text-primary">
                                        {audit.tier}
                                    </span>
                                    {!audit.classifiedTier && " (unclassified)"}
                                </span>
                                <span>
                                    Crop{" "}
                                    <span className="font-semibold text-primary">
                                        {audit.cropProfile}
                                    </span>
                                </span>
                                <span>
                                    Status{" "}
                                    <span className="font-semibold text-primary">
                                        {audit.status}
                                    </span>
                                </span>
                                <span>
                                    {audit.passes.length} model call
                                    {audit.passes.length === 1
                                        ? ""
                                        : "s"} · {audit.rounds.length} retry
                                    round
                                    {audit.rounds.length === 1 ? "" : "s"}
                                </span>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 text-xs"
                                onClick={copy}
                            >
                                {copied ? (
                                    <Check className="mr-1 h-3 w-3" />
                                ) : (
                                    <Copy className="mr-1 h-3 w-3" />
                                )}
                                {copied ? "Copied" : "Copy JSON"}
                            </Button>
                        </div>

                        {audit.error && (
                            <div className="rounded border border-gray-100 bg-gray-50 p-2 text-xs text-[#d50000]">
                                Caption run failed: {audit.error}
                            </div>
                        )}

                        <pre className="max-h-[55vh] overflow-auto rounded border border-gray-100 bg-gray-50 p-3 text-[11px] leading-relaxed text-primary">
                            {json}
                        </pre>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default CaptionAuditDialog;
